const KINDS = ['intro', 'planned', 'extra', 'followup', 'closing'];
function parseQuestion(q) {
    if (!q || typeof q !== 'object')
        return null;
    const id = typeof q.id === 'string' ? q.id : '';
    const text = typeof q.text === 'string' ? q.text.trim() : '';
    if (!id || !text)
        return null;
    return {
        id,
        text,
        kind: KINDS.includes(q.kind) ? q.kind : 'followup',
        label: typeof q.label === 'string' && q.label ? q.label : 'Question',
    };
}
/** Add or update `q` in the asked-questions list, keeping the order questions were first asked. */
export function upsertAskedQuestion(list, q) {
    if (!q)
        return list;
    const i = list.findIndex((x) => x.id === q.id);
    if (i === -1)
        return [...list, q];
    if (list[i].text === q.text && list[i].label === q.label)
        return list;
    const next = list.slice();
    next[i] = q;
    return next;
}
const count = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};
/** The message, or null when it is not a usable one. */
export function parseQuestionProgress(data) {
    var _a;
    if (!data || typeof data !== 'object')
        return null;
    const plannedTotal = count(data.planned_total);
    const plannedDone = count(data.planned_done);
    const current = count(data.current);
    if (plannedTotal == null || plannedDone == null || current == null)
        return null;
    return {
        plannedTotal,
        plannedDone: Math.min(plannedDone, plannedTotal),
        current: Math.min(current, plannedTotal),
        extraAsked: (_a = count(data.extra_asked)) !== null && _a !== void 0 ? _a : 0,
        currentIsExtra: !!data.current_is_extra,
        question: parseQuestion(data.question),
    };
}
/** The planned part: "Question 3 of 8", "8 questions planned", "All 8 questions done". */
export function plannedLabel(p) {
    if (p.plannedTotal <= 0)
        return null;
    const noun = p.plannedTotal === 1 ? 'question' : 'questions';
    if (p.current > 0 && p.plannedDone < p.plannedTotal)
        return `Question ${p.current} of ${p.plannedTotal}`;
    if (p.plannedDone >= p.plannedTotal)
        return `All ${p.plannedTotal} ${noun} done`;
    return `${p.plannedTotal} ${noun} planned`;
}
/** The extra part, only once one has been asked: "Extra questions: 2". */
export function extraLabel(p) {
    return p.extraAsked > 0 ? `Extra questions: ${p.extraAsked}` : null;
}
/**
 * VA-29073: what the interview screen shows as questions.
 *
 * With the server's question list (cost-optimized): one entry per QUESTION, and the
 * current one is the server's — so acknowledgements, reminders and "I can hear you
 * clearly" are never shown as a question or given a tile. Without it (realtime,
 * or before the first question arrives): the previous behaviour, every assistant
 * message, with the live transcript while the assistant is speaking.
 */
export function screenQuestions(input) {
    var _a, _b, _c, _d, _e, _f, _g;
    const asked = (_a = input.asked) !== null && _a !== void 0 ? _a : [];
    if (asked.length > 0) {
        const currentId = (_c = (_b = input.progress) === null || _b === void 0 ? void 0 : _b.question) === null || _c === void 0 ? void 0 : _c.id;
        const found = asked.findIndex((q) => q.id === currentId);
        const currentIdx = found === -1 ? asked.length - 1 : found;
        return {
            questions: asked,
            currentIdx,
            currentText: ((_e = (_d = input.progress) === null || _d === void 0 ? void 0 : _d.question) === null || _e === void 0 ? void 0 : _e.text) || ((_f = asked[currentIdx]) === null || _f === void 0 ? void 0 : _f.text) || '',
            fromServer: true,
        };
    }
    const questions = input.aiMessages.map((text, i) => ({ id: `m-${i}`, text, label: `Question ${i + 1}` }));
    return {
        questions,
        currentIdx: questions.length - 1,
        currentText: input.liveBot || ((_g = questions[questions.length - 1]) === null || _g === void 0 ? void 0 : _g.text) || '',
        fromServer: false,
    };
}
/** The panel heading: "Question 3 of 8", or "Follow-up · Question 3 of 8". */
export function currentQuestionHeading(progress, fromServer) {
    const q = progress === null || progress === void 0 ? void 0 : progress.question;
    if (!fromServer || !q || !progress)
        return 'Current question';
    // The closing Q&A heads itself; "All 8 questions done" says the rest.
    const planned = q.kind !== 'planned' ? plannedLabel(progress) : null;
    return [q.label, planned].filter(Boolean).join(' · ');
}
